import { NextPage } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import styled from '@emotion/styled';
import { useIsMobile } from '@hooks/responsive/useIsMobile';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

// components
import DefaultLayout from '@components/_templates/DefaultLayout';
import Typography from '@components/_atoms/Typography';
import Button from '@components/_atoms/Button';
import WarningIcon from '@public/assets/3dIcons/3dRedWarning.svg';

// constants
import { MOBILE_MODE } from '@constants/size';
import { ButtonColor, TypoVariant } from '@constants/atoms';
import { gray, primary } from '@styles/Colors';
import { CHANNEL_TALK } from '@constants/routes/routes';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useResendEmailFetch } from '@hooks/fetch/useAuthFetch';
import { Toast } from '@components/_atoms/Toast';
import ButtonCode from '@constants/log/buttonCode';

const NeedWrapper = styled.div`
  width: 100%;
  max-width: 528px;
  padding: 336px 0;
  margin: 0 auto;
  text-align: center;

  @media ${MOBILE_MODE} {
    max-width: 320px;
    padding: 200px 0;
  }
`;

const TitleWrapper = styled.div`
  margin-top: 24px;
`;

const ContentWrapper = styled.div`
  margin-top: 8px;
`;

const ButtonWrapper = styled.div`
  margin-top: 80px;

  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 8px;

  @media ${MOBILE_MODE} {
    align-items: stretch;
    margin-top: 108px;
  }
`;

const Need: NextPage = () => {
  const { t, i18n } = useTranslation('auth');
  const ko = i18n.language === 'ko';
  const isMobile = useIsMobile();

  const router = useRouter();
  const { email } = router.query as { email: string };

  const { resendEmailFetchMutate } = useResendEmailFetch();

  const handleResendEmail = () => {
    resendEmailFetchMutate(
      { email },
      {
        onSuccess: () => {
          Toast.success(t('signup.need.resend-message'));
        },
      },
    );
  };

  return (
    <>
      <Head>
        <title>{t('signup.need.meta-title')}</title>
        <meta name="title" content={t('signup.need.meta-title')} />
      </Head>
      <DefaultLayout>
        <NeedWrapper>
          <WarningIcon />
          <TitleWrapper>
            <Typography variant={isMobile ? TypoVariant.SH2 : TypoVariant.H6} color={primary.gray}>
              {t('signup.need.title')}
            </Typography>
          </TitleWrapper>
          {email && (
            <ContentWrapper>
              <Typography variant={isMobile ? TypoVariant.B3 : TypoVariant.B2} color={gray.gray7}>
                {ko ? (
                  <>
                    가입하신 이메일은
                    <br />
                    {email} 이에요.
                    <br />
                    이메일 인증을 해주세요.
                  </>
                ) : (
                  <>
                    The email you used to sign up is
                    <br />
                    {email}.
                    <br />
                    Please verify your email.
                  </>
                )}
              </Typography>
            </ContentWrapper>
          )}
          <ButtonWrapper>
            <Button
              onClick={handleResendEmail}
              text={t('signup.need.reSubmit-button')}
              color={ButtonColor.OutlinedGray}
              steelbosoId={ButtonCode.BTN_SUBMIT}
            />
            <a href={CHANNEL_TALK} target="_blank" rel="noreferrer">
              <Button
                width="100%"
                text={t('signup.need.channelTalk-button')}
                color={ButtonColor.TransparentGray}
                steelbosoId={ButtonCode.BTN_CHANNEL_TALK}
              />
            </a>
          </ButtonWrapper>
        </NeedWrapper>
      </DefaultLayout>
    </>
  );
};

export const getStaticProps = async ({ locale }: { locale: any }) => ({
  props: {
    ...(await serverSideTranslations(locale, ['auth', 'common', 'component'])),
  },
});

export default Need;
